/**
 * WS-BG — resolving a Background CHOICE into the sRGB colour the render paints.
 *
 * `render_settings.background` is a choice KEY (`transparent | solid | gradient | image |
 * brand_color`). Two of those choices name a colour that only the CLIENT can resolve: `solid`
 * is the operator's own pick, and `brand_color` is the workspace brand's `color_primary`. The
 * resolved value is stored alongside the key as `render_settings.background_color`, and BOTH
 * render paths read it — the classic mask render (`wireBackgroundToEngine`) and the
 * baked/layered mock-up compositor.
 *
 * **The resolution happens at SAVE time, never at render time.** A render must be reproducible
 * from the stored payload alone: the batch/queue path renders on other devices, days later,
 * from a LOCKED version, and has no brand list to consult. The consequence is the rule the UI
 * states out loud — recolouring a brand does NOT retint already-saved templates; each template
 * picks the new colour up on its NEXT Save, because every Save re-resolves `brand_color`
 * instead of copying the previously stored value.
 */
import type { BrandProfile } from "@genz/platform";

/** sRGB `[r, g, b, a]`, 0..255 — the wire shape of `render_settings.background_color`. */
export type BackgroundRgba = [number, number, number, number];

/** The colour a freshly-picked "Solid color" starts on: a neutral studio grey, not white.
 *  White would be indistinguishable from the legacy "no background" canvas, so an operator
 *  could not tell whether their pick had taken effect. */
export const DEFAULT_SOLID_BACKGROUND_HEX = "#f2f2f2";

/** `"#rrggbb"` → opaque `[r, g, b, 255]`; `null` when it is not a 6-digit hex colour. */
export function hexToBackgroundRgba(hex: string | null | undefined): BackgroundRgba | null {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex ?? "").trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
}

/** `[r, g, b, a]` → `"#rrggbb"` (alpha dropped — `<input type="color">` has no alpha). */
export function backgroundRgbaToHex(c: BackgroundRgba | null | undefined): string {
  if (!c || c.length < 3) return DEFAULT_SOLID_BACKGROUND_HEX;
  return "#" + c.slice(0, 3)
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0"))
    .join("");
}

/**
 * The brand whose `color_primary` the `brand_color` choice means: the workspace's **DEFAULT**
 * brand, and only that one.
 *
 * F5 — there is no fallback to "the first brand that happens to have a colour". The UI, the
 * `InfoHint` and `docs/frontend-flow.md` all say "the default brand", and a silent fallback
 * makes that sentence false in exactly the case an operator cannot see: a workspace with three
 * brands where the default has no colour set would publish templates in some OTHER brand's
 * colour, and the only way to find out is to look at an export. Answering `null` instead sends
 * the operator to Branding with a warning naming what to fix.
 *
 * Rejected/withdrawn brands are skipped — a template must not ship a colour from an identity the
 * workspace has revoked. `status` is absent on pre-017 records, which are treated as approved
 * (the same rule the rest of the app applies to them).
 */
export function brandColorPrimary(brands: readonly BrandProfile[] | null | undefined): string | null {
  const def = (brands ?? []).find(
    (b) => b.is_default && (b.status === undefined || b.status === "approved"),
  );
  return def?.color_primary || null;
}

/**
 * The `background_color` to STORE for a Background choice, or `null` when the choice carries
 * no colour of its own.
 *
 *  * `solid` — the operator's picked colour (falling back to the neutral default rather than
 *    writing nothing, so a Solid template is never silently white).
 *  * `brand_color` — the resolved brand colour; `null` when no usable brand colour exists, so
 *    the template stays on white instead of inventing a swatch.
 *  * `transparent` — `null`. The KEY alone already means "empty canvas" everywhere
 *    (`wireBackgroundToEngine`), and storing a second, redundant source of truth for it is how
 *    the two come to disagree.
 *  * `gradient` / `image` / unset — `null`: they are carried by `background_gradient` and the
 *    baked background image respectively.
 */
export function resolveBackgroundColor(
  choice: string | null | undefined,
  opts: { solidHex?: string | null; brandColor?: string | null } = {},
): BackgroundRgba | null {
  switch (choice) {
    case "solid":
      return hexToBackgroundRgba(opts.solidHex) ?? hexToBackgroundRgba(DEFAULT_SOLID_BACKGROUND_HEX);
    case "brand_color":
      return hexToBackgroundRgba(opts.brandColor);
    default:
      return null;
  }
}
